import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Calculator, Calendar, Loader2 } from 'lucide-react';
import type { Asset, AssetYearValue } from '@/types/asset';
import type { Currency } from '@/types/financials';
import { dataService } from '@/services/data-service';
import { useIntegrationService } from '@/hooks/use-integration-service';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Helper function to get revaluation cell class - extracted to fix nested ternary (S3358)
function getRevaluationCellClass(revaluationValue: number): string {
  if (revaluationValue > 0) {
    return "text-blue-600 font-medium";
  }
  if (revaluationValue < 0) {
    return "text-orange-600 font-medium";
  }
  return "";
}

// Helper function to get cell class based on value and type - reduces cognitive complexity (S3776)
function getValueCellClass(value: number, type: 'positive' | 'negative'): string {
  if (value <= 0) return "";
  return type === 'positive' ? "text-green-600 font-medium" : "text-red-600 font-medium";
}

// Skeleton field identifiers for stable keys - fixes S6479 (no array index in keys)
const SKELETON_FIELDS = ['field-1', 'field-2', 'field-3', 'field-4'] as const;

// Loading skeleton component - extracted to reduce cognitive complexity (S3776)
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SKELETON_FIELDS.map((fieldId) => (
              <Skeleton key={fieldId} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Empty state component - extracted to reduce cognitive complexity (S3776)
function EmptyYearValuesState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium text-muted-foreground">No year values available</p>
        <p className="text-sm text-muted-foreground mt-2">
          Year values will appear here once the asset has financial data.
        </p>
      </CardContent>
    </Card>
  );
}

interface AssetDetailValuesProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
  readonly isCreateMode?: boolean;
  readonly errors?: Record<string, string>;
}

export function AssetDetailValues({ asset, onAssetUpdate, isEditMode = false, isCreateMode = false, errors = {} }: Readonly<AssetDetailValuesProps>) {
  const { service: integrationService } = useIntegrationService();
  const [yearValues, setYearValues] = useState<AssetYearValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState(false);

  useEffect(() => {
    const loadYearValues = async () => {
      setIsLoading(true);
      try {
        // Only load year values if asset has an ID (not in create mode)
        if (asset.id && !asset.id.startsWith('new-')) {
          const values = await dataService.getAssetYearValues(asset.id, false);
          setYearValues(values);
        } else {
          setYearValues([]);
        }
      } catch (error) {
        console.error('Failed to load year values:', error);
        setYearValues([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadYearValues();
  }, [asset.id]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      setCurrenciesLoading(true);
      setCurrenciesError(false);
      try {
        const data = await integrationService.getCurrencies();
        setCurrencies(data);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
        setCurrenciesError(true);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    fetchCurrencies();
  }, [integrationService]);

  const handleFieldChange = (field: keyof Asset, value: any) => {
    onAssetUpdate({ ...asset, [field]: value });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: asset.currency || 'GBP',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatCurrencyDisplay = (isoCode: string, description?: string | null) => {
    if (description) {
      return `${isoCode} - ${description}`;
    }
    return isoCode;
  };

  const getSelectedCurrencyDisplay = () => {
    if (currenciesLoading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (asset.currency) {
      const selectedCurrency = currencies.find(c => c.isoCode === asset.currency);
      return selectedCurrency
        ? formatCurrencyDisplay(selectedCurrency.isoCode, selectedCurrency.description)
        : asset.currency;
    }
    return "Select currency...";
  };

  // Extracted to reduce cognitive complexity (S3776)
  const renderCurrencySelector = (labelRequired: boolean) => (
    <div className="space-y-2">
      <Label htmlFor="currency">Currency {labelRequired && <span className="text-destructive">*</span>}</Label>
      {isEditMode && isCreateMode ? (
        <Select
          value={asset.currency || 'GBP'}
          onValueChange={(value: string) => handleFieldChange('currency', value)}
          disabled={currenciesLoading}
        >
          <SelectTrigger id="currency">
            <SelectValue>
              {getSelectedCurrencyDisplay()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {currencies.map((curr) => (
              <SelectItem key={curr.isoCode} value={curr.isoCode}>
                {formatCurrencyDisplay(curr.isoCode, curr.description)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id="currency"
          value={asset.currency || 'GBP'}
          readOnly
        />
      )}
      {currenciesError && (
        <p className="text-sm text-destructive">Failed to load currencies</p>
      )}
    </div>
  );

  // Get the current year (most recent)
  const currentYearValue = yearValues.length > 0 ? yearValues[yearValues.length - 1] : null;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // If no year values (create mode or new asset), show editable acquisition fields
  if (!currentYearValue) {
    return (
      <div className="space-y-6">
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Financial Information
            </CardTitle>
            <CardDescription className="text-xs">
              Set the initial acquisition value and currency for this asset
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Acquisition Cost */}
            <div className="space-y-2">
              <Label htmlFor="acquisitionCost">Acquisition Value <span className="text-destructive">*</span></Label>
              {isEditMode ? (
                <CurrencyInput
                  id="acquisitionCost"
                  value={asset.acquisitionCost ?? 0}
                  onChange={(value: number) => handleFieldChange('acquisitionCost', value)}
                  className={errors.acquisitionCost ? 'border-destructive' : ''}
                />
              ) : (
                <Input
                  id="acquisitionCost"
                  value={formatCurrency(asset.acquisitionCost ?? 0)}
                  readOnly
                />
              )}
              {errors.acquisitionCost && <p className="text-sm text-destructive">{errors.acquisitionCost}</p>}
            </div>

            {/* Currency */}
            {renderCurrencySelector(true)}
          </CardContent>
        </Card>

        {!isEditMode && <EmptyYearValuesState />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial Information Card */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Financial Information
          </CardTitle>
          <CardDescription className="text-xs">
            Current financial summary and asset valuation
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Acquisition Cost */}
          <div className="space-y-2">
            <Label htmlFor="acquisitionCost">Acquisition Value</Label>
            <Input
              id="acquisitionCost"
              value={formatCurrency(asset.acquisitionCost ?? 0)}
              readOnly
            />
          </div>

          {/* Currency */}
          {renderCurrencySelector(false)}

          {/* Net Book Value */}
          <div className="space-y-2">
            <Label htmlFor="netBookValue">Net Book Value</Label>
            <Input
              id="netBookValue"
              value={formatCurrency(asset.netBookValue ?? 0)}
              readOnly
            />
          </div>

          {/* Accumulated Depreciation */}
          <div className="space-y-2">
            <Label htmlFor="accumulatedDepreciation">Accumulated Depreciation</Label>
            <Input
              id="accumulatedDepreciation"
              value={formatCurrency(asset.accumulatedDepreciation ?? 0)}
              readOnly
            />
          </div>
        </CardContent>
      </Card>

      {/* Year Values Section */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Year Values - {currentYearValue.year}
          </CardTitle>
          <CardDescription className="text-xs">
            Detailed breakdown of cost, depreciation, and net book value movements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Net Book Value Section - Shown First */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Net Book Value
            </h3>
            <div className="flex gap-8">
              <div className="space-y-1.5">
                <Label htmlFor="nbvStartOfYear" className="text-xs text-muted-foreground">Start of Year</Label>
                <div className="text-lg font-semibold">
                  {formatCurrency(currentYearValue.netBookValue.startOfYear)}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="nbvEndOfYear" className="text-xs text-muted-foreground">End of Year</Label>
                <div className="text-lg font-semibold text-green-600">
                  {formatCurrency(currentYearValue.netBookValue.endOfYear)}
                </div>
              </div>
            </div>
          </div>

          {/* Cost Section - Table Format */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Cost
            </h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[20%]">Start of Year</TableHead>
                    <TableHead className="w-[20%]">Additions</TableHead>
                    <TableHead className="w-[20%]">Disposals</TableHead>
                    <TableHead className="w-[20%]">Revaluation</TableHead>
                    <TableHead className="w-[20%] font-semibold">End of Year</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-semibold">
                      {formatCurrency(currentYearValue.cost.startOfYear)}
                    </TableCell>
                    <TableCell className={getValueCellClass(currentYearValue.cost.additions, 'positive')}>
                      {formatCurrency(currentYearValue.cost.additions)}
                    </TableCell>
                    <TableCell className={getValueCellClass(currentYearValue.cost.disposals, 'negative')}>
                      {formatCurrency(currentYearValue.cost.disposals)}
                    </TableCell>
                    <TableCell className={getRevaluationCellClass(currentYearValue.cost.revaluation)}>
                      {formatCurrency(currentYearValue.cost.revaluation)}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(currentYearValue.cost.endOfYear)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Depreciation Section - Table Format */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Depreciation
            </h3>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[20%]">Start of Year</TableHead>
                    <TableHead className="w-[20%]">Charge</TableHead>
                    <TableHead className="w-[20%]">Disposals</TableHead>
                    <TableHead className="w-[20%]">Revaluation</TableHead>
                    <TableHead className="w-[20%] font-semibold">End of Year</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-semibold">
                      {formatCurrency(currentYearValue.depreciation.startOfYear)}
                    </TableCell>
                    <TableCell className={getValueCellClass(currentYearValue.depreciation.charge, 'negative')}>
                      {formatCurrency(currentYearValue.depreciation.charge)}
                    </TableCell>
                    <TableCell className={getValueCellClass(currentYearValue.depreciation.disposals, 'positive')}>
                      {formatCurrency(currentYearValue.depreciation.disposals)}
                    </TableCell>
                    <TableCell className={getRevaluationCellClass(currentYearValue.depreciation.revaluation)}>
                      {formatCurrency(currentYearValue.depreciation.revaluation)}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(currentYearValue.depreciation.endOfYear)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
